import dayjs from "dayjs";
import * as React from "react";
import { CloudService, IBuildDto } from "../../../../services/cloud-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./cloud-deployments.scss";

type DeploymentRow = {
  id: string;
  project: string;
  environment: string;
  lastDeploymentAt: string;
  lastBuildNumber: number;
  lastBuildResult: string;
  deploymentChannel?: string;
};

interface ProjectBuildSummary {
  project: string;
  lastDeploymentAt: string;
  lastBuildNumber: number;
  lastBuildResult: string;
  deploymentChannel?: string;
}

interface CloudDeploymentsState {
  searchText: string;
  projects: ProjectBuildSummary[];
  loading: boolean;
  error?: string;
}

class CloudDeploymentsBase extends React.Component<IWithHistoryProp, CloudDeploymentsState> {
  constructor(props: IWithHistoryProp) {
    super(props);
    this.state = {
      searchText: "",
      projects: [],
      loading: false,
      error: undefined
    };
  }

  componentDidMount() {
    this.fetchProjectsWithBuilds();
  }

  fetchProjectsWithBuilds = async () => {
    this.setState({ loading: true, error: undefined });

    try {
      const response = await CloudService.GetBuilds(undefined, 1000);

      if (response && response.data) {
        const projectSummaries = this.processBuildsToProjectSummaries(response.data);
        this.setState({ projects: projectSummaries, loading: false });
      } else {
        throw new Error("No data received from cloud service");
      }
    } catch (error) {
      console.error("Error fetching projects with builds:", error);
      this.setState({
        error: error instanceof Error ? error.message : "Failed to fetch projects with builds",
        loading: false
      });
    }
  };

  processBuildsToProjectSummaries = (builds: IBuildDto[]): ProjectBuildSummary[] => {
    const projectBuilds: { [key: string]: IBuildDto[] } = {};

    builds.forEach((build) => {
      if (!build.project || build.project.trim() === "") return;

      const channel = build.deploymentChannel ?? "web";
      const key = `${build.project}::${channel}`;

      if (!projectBuilds[key]) {
        projectBuilds[key] = [];
      }
      projectBuilds[key].push(build);
    });

    const projectSummaries: ProjectBuildSummary[] = [];

    Object.entries(projectBuilds).forEach(([key, projectBuildList]) => {
      // Sort builds by number descending to get the latest builds first
      const sortedBuilds = projectBuildList.sort((a, b) => b.number - a.number);

      const [project, channel] = key.split("::");

      // Find the latest successful build, or use the latest build if no success
      let latestDeployment = sortedBuilds.find((build) => build.result === "SUCCESS" || build.result === "STABLE");

      // If no successful build found, use the latest build
      if (!latestDeployment && sortedBuilds.length > 0) {
        latestDeployment = sortedBuilds[0];
      }

      if (latestDeployment) {
        projectSummaries.push({
          project,
          lastDeploymentAt: latestDeployment.date,
          lastBuildNumber: latestDeployment.number,
          lastBuildResult: latestDeployment.result,
          deploymentChannel: channel
        });
      }
    });

    // Sort projects by latest deployment date descending
    return projectSummaries.sort(
      (a, b) => new Date(b.lastDeploymentAt).getTime() - new Date(a.lastDeploymentAt).getTime()
    );
  };

  handleSearchChange = (_: any, value: string | number) => {
    this.setState({ searchText: String(value || "") });
  };

  getDeploymentData = (): DeploymentRow[] => {
    const { projects } = this.state;

    return projects.map((projectSummary, index) => ({
      id: `project-${index}`,
      project: projectSummary.project,
      environment: projectSummary.deploymentChannel ? projectSummary.deploymentChannel.toUpperCase() : "PRODUCTION",
      lastDeploymentAt: projectSummary.lastDeploymentAt,
      lastBuildNumber: projectSummary.lastBuildNumber,
      lastBuildResult: projectSummary.lastBuildResult,
      deploymentChannel: projectSummary.deploymentChannel
    }));
  };

  getFilteredData = (): DeploymentRow[] => {
    const { searchText } = this.state;
    const data = this.getDeploymentData();

    if (!searchText) return data;
    const s = searchText.toLowerCase();
    return data.filter((x) => x.project.toLowerCase().includes(s) || x.environment.toLowerCase().includes(s));
  };

  getBuildStatusColor = (result: string): string => {
    switch (result.toUpperCase()) {
      case "SUCCESS":
      case "STABLE":
        return "var(--green-6)";
      case "FAILURE":
      case "FAILED":
        return "var(--red-6)";
      case "UNSTABLE":
        return "var(--yellow-6)";
      case "IN_PROGRESS":
      case "BUILDING":
        return "var(--blue-6)";
      default:
        return "var(--dark-4)";
    }
  };

  render() {
    const { navigator } = this.props;
    const { searchText, loading, error } = this.state;
    const filteredData = this.getFilteredData();

    const columns: KuikaTableColumn<DeploymentRow>[] = [
      {
        id: "project",
        accessorKey: "project",
        enableSorting: true,
        header: () => "Project name",
        cell: (row) => {
          const handleNavigate = () =>
            navigator.navigate(
              `/${navigator.getCurrentDomain()}/cloud/deployments/${encodeURIComponent(row.project)}`,
              {
                state: { deploymentRow: row }
              }
            );
          return (
            <button
              className="cloud-deployments__link-button"
              onClick={handleNavigate}
              aria-label={`Open deployments / ${row.project}`}
            >
              <KuikaTextMedium className="cloud-deployments__link-text">{row.project}</KuikaTextMedium>
            </button>
          );
        }
      },
      {
        id: "environment",
        accessorKey: "environment",
        enableSorting: true,
        width: 120,
        header: () => "Channel",
        cell: (row) => (row.environment ? row.environment.charAt(0) + row.environment.slice(1).toLowerCase() : "")
      },
      {
        id: "lastBuildStatus",
        accessorKey: "lastBuildResult",
        enableSorting: true,
        width: 120,
        header: () => "Last Build",
        cell: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "var(--radius-pill)",
                backgroundColor: this.getBuildStatusColor(row.lastBuildResult)
              }}
            />
            <span>#{row.lastBuildNumber}</span>
          </div>
        )
      },
      {
        id: "lastDeploymentAt",
        accessorFn: (row) => row.lastDeploymentAt,
        enableSorting: true,
        width: 180,
        header: () => "Last Deployment",
        cell: (row) => {
          try {
            return dayjs(row.lastDeploymentAt).format("DD.MM.YYYY - HH:mm:ss");
          } catch {
            return row.lastDeploymentAt;
          }
        }
      },
      {
        id: "actions",
        enableSorting: false,
        width: 32,
        align: "right",
        header: () => <></>,
        cell: () => (
          <KuikaButton type="tertiary" size="sm" isIcon>
            <KuikaSvg name="dots" width={16} height={16} pointer />
          </KuikaButton>
        )
      }
    ];

    return (
      <div className="kuika-settings-panel">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="rocket" />
            <KuikaH4>Deployments</KuikaH4>
          </div>
          <div className="kuika-settings-panel__header-right">
            <KuikaInput
              aria-label="Search deployments"
              placeholder="Search projects"
              value={searchText}
              onChange={this.handleSearchChange}
              leftIcon={<KuikaSvg name="search" width={14} height={14} color="var(--dark-5)" />}
              style={{ width: 240 }}
              tabIndex={0}
            />
          </div>
        </div>
        <div className="cloud-deployments__content">
          {error && (
            <div className="cloud-deployments__error" style={{ padding: "16px", color: "var(--red-6)" }}>
              Error: {error}
              <KuikaButton
                type="secondary"
                size="sm"
                onClick={this.fetchProjectsWithBuilds}
                style={{ marginLeft: "8px" }}
              >
                Retry
              </KuikaButton>
            </div>
          )}
          {!error && (
            <KuikaTable<DeploymentRow>
              data={filteredData}
              columns={columns}
              rowKey={(row) => row.id}
              showCountLabel="Projects"
              enableSorting
              loading={loading}
              emptyIcon={<KuikaSvg name="rocket" width={48} height={48} style={{ opacity: 0.5 }} />}
              emptyTitle="No deployments yet"
              emptyDescription="Deploy your first project to get started"
            />
          )}
        </div>
      </div>
    );
  }
}

const CloudDeployments = withManagement(withHistory(CloudDeploymentsBase));
export { CloudDeployments };
